import Button from '@atlaskit/button';
import AddIcon from '@atlaskit/icon/glyph/add';
import { useDatabaseContent } from '@databases/database-state';
import { useFlaggingCallback } from '@databases/flags';
import { messages } from '@databases/intl/messages';
import usePanel from '@databases/panel/usePanel';
import { isModifierKeyPressed } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { appStoreUtils } from '@databases/redux/store';
import { SkeletonItem } from '@databases/skeleton';
import { DatabaseChangeType } from '@databases/utils/database/changesets';
import { createEntry } from '@databases/utils/database/utils';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import useView from '@databases/views/useView';
import React, { forwardRef, Ref } from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import EntryCardContainer, {
  entryCardShadowProps,
} from '../entry-card/EntryCardContainer';

const StyledSkeletonItem = styled(SkeletonItem)`
  &&& {
    display: flex;
    flex-direction: column;
  }
`;

const StyledEntryCardContainer = styled(EntryCardContainer)`
  flex-grow: 1;
  width: 100%;
  padding: 0;
  min-height: 32px;
`;

const StyledButton = styled(Button)`
  ${entryCardShadowProps}

  && {
    height: auto;
    align-items: center;

    > * {
      flex-grow: 0;
    }
  }
`;

interface AddEntryCardProps {
  onAdd: () => void;
}

const AddEntryCard = forwardRef<HTMLElement, AddEntryCardProps>(function AddEntryCard(
  { onAdd },
  ref
) {
  const { showPanel } = usePanel();

  const scope = 'main';
  const { view } = useView();
  const { contentId } = useDatabaseContent() as { contentId: string };
  const dispatch = appStoreUtils.useDispatch();

  const { formatMessage } = useIntl();

  const database = defaultStoreUtils.useSelector((state) => {
    return selectDatabaseByContentIdRequired(state, { contentId });
  });

  const handleClick = useFlaggingCallback(
    async (e: React.MouseEvent | React.KeyboardEvent) => {
      if (!isModifierKeyPressed(e)) {
        onAdd();
        e.preventDefault();

        const entry = createEntry(database, view.settings);
        dispatch(
          addChangeToDraft(contentId, scope, {
            type: DatabaseChangeType.ADD_ENTRY,
            entry,
          })
        );

        showPanel('EntryViewPanel', {
          contentId,
          entryId: entry.id,
        });
      }
    },
    [contentId, database, dispatch, onAdd, showPanel, view.settings]
  );

  return (
    <StyledSkeletonItem isButton>
      <StyledEntryCardContainer ref={ref as Ref<HTMLDivElement>}>
        <StyledButton
          onClick={handleClick}
          iconBefore={<AddIcon label="" />}
          appearance="subtle"
        >
          {formatMessage(messages['entry-card.add-entry-text'])}
        </StyledButton>
      </StyledEntryCardContainer>
    </StyledSkeletonItem>
  );
});

export default AddEntryCard;
